/**
 * GMRosterBuilderV2.jsx
 * 
 * Phase 3B - COMPOSITION ROOT
 * 
 * This is now a TRUE CONTAINER that:
 * - Manages state
 * - Switches subViews via imported view components
 * - Passes props to child components
 * 
 * All view JSX has been extracted to:
 * - features/roster/views/PortfolioView.jsx
 * - features/roster/views/ReviewView.jsx
 * - features/roster/views/ScoutView.jsx
 * - features/roster/views/PropSelectionView.jsx
 * 
 * BSL Compliant: No edge computation, displays server values only.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';

// Extracted view components
import { 
  PortfolioView, 
  ReviewView, 
  ScoutView, 
  PropSelectionView 
} from '../../features/roster/views';

// Data hook
import { useActiveSlate } from '../../hooks/useActiveSlate';

// Centralized filtering
import { filterPlayers } from '../../utils/transformers';
import { buildRosterPayload } from '../../hooks/useGMStrategy';

// Constants
import { INITIAL_ROSTER } from '../../constants';

// Phase 1A: Live Outcomes polling
import { useLiveOutcomesPolling } from '../../hooks/useLiveOutcomesPolling';

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN CONTAINER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * GMRosterBuilderV2 - Slot-based roster builder container
 * 
 * @param {Object} props
 * @param {Object} props.pool - Pool configuration
 * @param {Array} [props.initialRoster] - Initial roster state
 * @param {boolean} [props.isLocked=false] - Whether roster is locked
 * @param {function} props.saveEntry - Save draft function
 * @param {function} props.lockEntry - Lock roster function
 * @param {function} props.onBack - Back navigation handler
 * @param {function} props.onSuccess - Success callback after lock
 * @param {function} props.showToast - Toast notification function
 */
const GMRosterBuilderV2 = ({
  pool,
  availableBalance = 1000,
  initialRoster,
  isLocked = false,
  saveEntry, 
  lockEntry, 
  onBack, 
  onSuccess,
  showToast 
}) => {
  // ===== STATE =====
  
  const [subView, setSubView] = useState('portfolio');
  const [roster, setRoster] = useState(initialRoster || INITIAL_ROSTER);
  
  // ===== PHASE 1A: LIVE OUTCOMES POLLING =====
  const userId = localStorage.getItem('userId') || 'test_user_001';
  const poolId = pool?.pool_id || pool?.id;
  
  const {
    roster: liveRoster,
    resolvedCount,
    totalSlots: liveTotalSlots,
    score: liveScore,
    lastUpdated,
    refresh: refreshOutcomes,
    isPolling,
    isFullyResolved
  } = useLiveOutcomesPolling(isLocked ? poolId : undefined, isLocked ? userId : undefined);
  
  // Use live roster when locked, local roster when building
  const displayRoster = isLocked && liveRoster?.length > 0 ? liveRoster : roster;
  const [activeSlot, setActiveSlot] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedProp, setSelectedProp] = useState(null);
  const [selectedSide, setSelectedSide] = useState(null);
  const [unitValue, setUnitValue] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    position: 'ALL',
    oppRank: 'ALL',
    showPositiveEdge: false
  });
  
  const [saving, setSaving] = useState(false);
  
  // ===== HYDRATE ROSTER FROM ENTRY =====
  
  useEffect(() => {
    if (initialRoster && Array.isArray(initialRoster) && initialRoster.length > 0) {
      const hasData = initialRoster.some(slot => slot.player);
      if (hasData) {
        setRoster(initialRoster);
      }
    }
  }, [initialRoster]);
  
  // ===== DATA HOOK =====
  
  const { 
    players, 
    playersForSlot, 
    loading: slateLoading, 
    error: slateError 
  } = useActiveSlate(pool?.pool_id || pool?.id);
  
  // ===== DERIVED VALUES =====
  
  const poolUnits = pool?.units || 100;
  const usedUnits = roster.reduce((s, r) => s + r.units, 0);
  // Calculate max units for current slot:
  // Available balance - units on other slots - (10u reserved per remaining empty slot)
  const unitsOnOtherSlots = usedUnits - (activeSlot?.units || 0);
  const emptySlots = roster.filter(s => !s.player && s.slot !== activeSlot?.slot).length;
  const availableUnits = Math.max(10, availableBalance - unitsOnOtherSlots - (emptySlots * 10));
  const filledSlots = roster.filter(s => s.player).length;
  const totalSlots = roster.length;
  const isReady = filledSlots === totalSlots && usedUnits <= availableBalance && usedUnits > 0;
  
  // Centralized filtering via transformers.filterPlayers
  const filteredPlayers = useMemo(() => {
    const basePlayers = activeSlot 
      ? playersForSlot(activeSlot.label) 
      : players;
    
    const list = filterPlayers(basePlayers, filters);
    const seen = new Map();
    list.forEach(p => { if (!seen.has(p.id)) seen.set(p.id, p); });
    return Array.from(seen.values());
  }, [players, activeSlot, filters, playersForSlot]);
  
  // ===== HANDLERS =====
  
  const handleEditSlot = useCallback((slot) => {
    if (isLocked) return;
    setActiveSlot(slot);
    setSelectedPlayer(slot.player);
    setSelectedProp(slot.prop);
    setSelectedSide(slot.side);
    setUnitValue(slot.units || 10);
    setFilters(prev => ({ ...prev, search: '' }));
    setSubView('scout');
  }, [isLocked]);
  
  const handleSelectPlayer = useCallback((player) => {
    setSelectedPlayer(player);
    setSelectedProp(null);
    setSelectedSide(null);
    setUnitValue(Math.min(10, availableUnits));
    setSubView('prop');
  }, [availableUnits]);
  
  const handleSelectProp = useCallback((prop, side) => {
    setSelectedProp(prop);
    setSelectedSide(side);
  }, []);
  
  const handleConfirm = useCallback(() => {
    if (!activeSlot || !selectedPlayer || !selectedProp || !selectedSide) return;
    
    const isDuplicate = roster.some(s => 
      s.player?.id === selectedPlayer?.id && s.slot !== activeSlot?.slot
    );
    if (isDuplicate) {
      showToast?.('Player already selected in another slot', 'error');
      return;
    }
    
    const updatedRoster = roster.map(s => 
      s.slot === activeSlot.slot 
        ? { ...s, player: selectedPlayer, prop: selectedProp, side: selectedSide, units: unitValue } 
        : s
    );
    
    setRoster(updatedRoster);
    
    if (saveEntry) {
      saveEntry(updatedRoster);
    }
    
    setActiveSlot(null);
    setSelectedPlayer(null);
    setSelectedProp(null);
    setSelectedSide(null);
    setSubView('portfolio');
  }, [activeSlot, selectedPlayer, selectedProp, selectedSide, unitValue, roster, saveEntry, showToast]);
  
  const handleSubBack = useCallback(() => {
    if (subView === 'prop') {
      setSubView('scout');
    } else if (subView === 'review') {
      setSubView('portfolio');
    } else {
      setActiveSlot(null);
      setSelectedPlayer(null);
      setSelectedProp(null);
      setSelectedSide(null);
      setSubView('portfolio');
    }
  }, [subView]);
  
  const handleLock = useCallback(async () => {
    console.log('[GMRosterBuilder] Attempting Lock...', { 
      isReady, 
      hasLockEntry: !!lockEntry 
    });

    if (!isReady) return;

    if (!lockEntry) {
      console.error('[GMRosterBuilder] CRITICAL ERROR: lockEntry prop is missing/undefined!');
      showToast?.('System Error: Lock function missing. Check console.', 'error');
      return;
    }

    setSaving(true);
    
    if (saveEntry) {
      const saveResult = await saveEntry(buildRosterPayload(roster));
      if (!saveResult?.success) {
        showToast?.(saveResult?.errors?.map(e => typeof e === 'object' ? (e.message || JSON.stringify(e)) : e).join(', ') || 'Failed to save', 'error');
        setSaving(false);
        return;
      }
    }
    
    const lockResult = await lockEntry();
    setSaving(false);
    
    if (lockResult?.success) {
      showToast?.('Roster locked!', 'success');
      onSuccess?.();
    } else {
      const lockErrorMsg = Array.isArray(lockResult?.errors)
        ? lockResult.errors.map(e => (typeof e === 'object' ? e.message : e)).join(', ')
        : 'Failed to lock roster';
      showToast?.(lockErrorMsg, 'error');
    }
  }, [isReady, roster, saveEntry, lockEntry, showToast, onSuccess]);
  
  const toRosterSlotSelection = useCallback((slot) => {
    if (!slot.player) return null;
    return {
      player: { name: slot.player.name, team: slot.player.team },
      prop: slot.prop ? { stat: slot.prop.stat, line: slot.prop.line } : null,
      side: slot.side,
      units: slot.units,
      resolution: slot.resolution || null,  // Phase 1A: Include resolution data
    };
  }, []);
  
  // ===== RENDER: LOADING/ERROR =====
  
  if (slateLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-indigo-600 font-medium">Loading players...</p>
        </div>
      </div>
    );
  }
  
  if (slateError) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 text-center max-w-sm">
          <p className="text-red-500 font-bold mb-2">Failed to Load Slate</p>
          <p className="text-slate-600 text-sm">{slateError}</p>
          <button onClick={onBack} className="mt-4 px-4 py-2 bg-slate-100 rounded-lg font-medium">
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  // ===== RENDER: VIEWS (COMPOSITION) =====
  
  if (subView === 'portfolio') {
    return (
      <PortfolioView
        roster={displayRoster}
        poolUnits={poolUnits}
        availableBalance={availableBalance}
        usedUnits={usedUnits}
        filledSlots={filledSlots}
        totalSlots={totalSlots}
        isReady={isReady}
        isLocked={isLocked}
        saving={saving}
        onEditSlot={handleEditSlot}
        onReview={() => setSubView('review')}
        onLock={handleLock}
        onBack={onBack}
        toRosterSlotSelection={toRosterSlotSelection}
        // Phase 1A: Live outcomes props
        liveScore={liveScore}
        resolvedCount={resolvedCount}
        lastUpdated={lastUpdated}
        onRefreshOutcomes={refreshOutcomes}
        isFullyResolved={isFullyResolved}
      />
    );
  }
  
  if (subView === 'review') {
    return (
      <ReviewView
        roster={roster}
        poolUnits={poolUnits}
        availableBalance={availableBalance}
        filledSlots={filledSlots}
        totalSlots={totalSlots}
        isReady={isReady}
        isLocked={isLocked}
        saving={saving}
        onBack={handleSubBack}
        onLock={handleLock}
      />
    );
  }
  
  if (subView === 'scout') {
    return (
      <ScoutView
        activeSlot={activeSlot}
        filteredPlayers={filteredPlayers}
        filters={filters}
        onFiltersChange={setFilters}
        onSelectPlayer={handleSelectPlayer}
        onBack={handleSubBack}
      />
    );
  }
  
  if (subView === 'prop') {
    return (
      <PropSelectionView
        selectedPlayer={selectedPlayer}
        selectedProp={selectedProp}
        selectedSide={selectedSide}
        unitValue={unitValue}
        availableUnits={availableUnits}
        onSelectProp={handleSelectProp}
        onUnitsChange={setUnitValue}
        onConfirm={handleConfirm}
        onBack={handleSubBack}
      />
    );
  }
  
  return null;
};

// Keep default export for backward compatibility during transition
export default GMRosterBuilderV2;
